import { useEffect, useState, useCallback } from "react";
import { X, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { EBOOK, openEbook, submitEbookLead, track } from "@/lib/ebook";

const DISMISS_KEY = "cc_ebook_popup_dismissed_v1";
const SCROLL_THRESHOLD = 0.35; // 35% of page
const TIME_THRESHOLD = 25_000; // 25s

export default function EbookPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const show = useCallback(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(DISMISS_KEY) === "1") return;
    if (open) return;
    setOpen(true);
    track("popup_view", "ebook-popup");
  }, [open]);

  const dismiss = useCallback((reason = "x") => {
    setOpen(false);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch (_) {}
    track("popup_dismiss", reason);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(DISMISS_KEY) === "1") return;

    let timer = setTimeout(() => show(), TIME_THRESHOLD);

    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled =
        (window.scrollY || doc.scrollTop) /
        ((doc.scrollHeight || 1) - window.innerHeight);
      if (scrolled > SCROLL_THRESHOLD) show();
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await submitEbookLead({ email, source: "popup" });
      toast.success("Your guide is opening in a new tab.");
      openEbook("popup");
      try {
        window.localStorage.setItem(DISMISS_KEY, "1");
      } catch (_) {}
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign up. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="ebook-popup"
      className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-6 sm:right-6 z-[60] max-w-full sm:max-w-md animate-in slide-in-from-bottom-8 fade-in duration-500"
    >
      <div className="relative bg-[#111111] border border-[#D4AF37]/40 sm:border-[#D4AF37]/30 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]">
        {/* Gold edge accent */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" aria-hidden />

        <button
          type="button"
          onClick={() => dismiss("x")}
          data-testid="ebook-popup-close"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/60 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-7 sm:p-8">
          <div className="flex items-center gap-2 overline text-[#D4AF37]">
            <Sparkles className="w-3.5 h-3.5" />
            Complimentary
          </div>
          <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight mt-3 text-white">
            Choose your photo<br />
            <span className="italic text-[#D4AF37]">like royalty.</span>
          </h3>
          <p className="text-white/65 text-sm leading-relaxed mt-4 font-light">
            Drop your email and we&apos;ll send you the Crowned Perfect Photo
            Guide — a short, premium read on selecting the perfect photo for
            custom artwork. No spam, just craft.
          </p>

          <form
            onSubmit={handleSubmit}
            data-testid="ebook-popup-form"
            className="mt-6 space-y-3"
          >
            <Input
              required
              type="email"
              data-testid="ebook-popup-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="bg-black/40 border-white/15 h-12 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="ebook-popup-submit"
              className="group inline-flex w-full items-center justify-center gap-3 bg-[#D4AF37] text-black h-12 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Send Me The Guide
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>
          <button
            type="button"
            onClick={() => dismiss("not-now")}
            data-testid="ebook-popup-skip"
            className="mt-3 w-full text-[11px] uppercase tracking-[0.22em] text-white/40 hover:text-white/70 transition-colors"
          >
            Not right now
          </button>
        </div>
      </div>
    </div>
  );
}
